// @givmo/fundraisers-module — fundraiser share panel (CPB-WEB1).
//
// The ACTIVE fundraiser's distribution kit: the public donate-page URL + the organization's canonical first-party
// short link (`go.givmo.io/s/<slug>`, per-env host), each with a clipboard copy button, plus svg/png/pdf
// QR downloads. Wiring: `orgFundraisersGetOrgFundraiserShare` (GET …/share → `{public_url, share_url,
// slug}`) for the links; `orgFundraisersRenderOrgFundraiserShareQr` (GET …/share/qr?format=…, binary) for
// the QR, via the shared blob-download helper (authenticated api-client, never a hand-rolled fetch).
//
// The parent renders this ONLY for an ACTIVE fundraiser (a DRAFT has no public page; the share endpoints
// require ACTIVE to mint) — we still gate the read with `enabled` so a mis-mount never fires it. Copyable
// links use the ENV's go-host (a dev/staging-minted slug does not exist in prod), unlike the printed QR
// payload which the SERVER renders with the literal-prod host (D-CQ-18/20) — we only *display* what the
// server returns; no host is interpolated here.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { orgFundraisersGetOrgFundraiserShareOptions } from '@givmo/api-client/query';
import { sdk, isGivmoError } from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Icon,
  Skeleton,
  testIdProp,
  toast,
} from '@givmo/design-system';
import { Copy, Download, Link2, Palette, QrCode, ShieldAlert } from 'lucide-react';
import { fallbackQrName, runQrDownload, type ShareQrFormat } from './share-download';
import { useLivePage } from './use-live-page';

const PREFIX = 'share';

const QR_FORMATS: readonly ShareQrFormat[] = ['svg', 'png', 'pdf'];

export interface SharePanelProps {
  orgRef: string;
  fundraiserId: string;
  /**
   * Opens the organization QR design editor for this fundraiser (FCM-WEB4). Absent ⇒ no "Customize"
   * — the host's knob is off, and the downloads below stay the organization's saved design.
   */
  onCustomize?: () => void;
}

export function SharePanel({ orgRef, fundraiserId, onCustomize }: SharePanelProps) {
  const live = useLivePage();
  const shareQuery = useQuery(
    orgFundraisersGetOrgFundraiserShareOptions({
      path: { org_ref: orgRef, fundraiser_id: fundraiserId },
    }),
  );

  const [downloading, setDownloading] = useState<ShareQrFormat | null>(null);

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      if (live()) toast.success(`${label} copied`);
    } catch {
      if (live())
        toast.error(`Could not copy the ${label.toLowerCase()}. Copy it manually instead.`);
    }
  };

  const downloadQr = async (format: ShareQrFormat) => {
    setDownloading(format);
    try {
      await runQrDownload(
        (options) =>
          sdk.orgFundraisersRenderOrgFundraiserShareQr({
            ...options,
            path: { org_ref: orgRef, fundraiser_id: fundraiserId },
            query: { format },
          }),
        fallbackQrName(format),
      );
      if (live()) toast.success('QR code downloaded');
    } catch (error) {
      if (!live()) return;
      toast.error(
        isGivmoError(error) ? error.message : 'Could not download the QR code. Please try again.',
      );
    } finally {
      if (live()) setDownloading(null);
    }
  };

  return (
    <Card testId={`${PREFIX}-panel`}>
      <CardHeader title="Share" subtitle="Send supporters to your fundraiser page." />
      <CardBody>
        {shareQuery.isPending ? (
          <div className="flex flex-col gap-10" {...testIdProp(`${PREFIX}-loading`)}>
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="h-12 w-1/2" />
          </div>
        ) : shareQuery.isError || !shareQuery.data ? (
          <EmptyState
            testId={`${PREFIX}-error`}
            icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
            title="Couldn’t load your share links"
            description={
              isGivmoError(shareQuery.error)
                ? shareQuery.error.message
                : 'We couldn’t load your fundraiser’s share links. Please try again.'
            }
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void shareQuery.refetch()}
                testId={`${PREFIX}-retry`}
              >
                Try again
              </Button>
            }
          />
        ) : (
          <div className="flex flex-col gap-16">
            {/* Public fundraiser page URL. */}
            <ShareLinkRow
              icon={Link2}
              label="Fundraiser page"
              value={shareQuery.data.public_url}
              onCopy={() => void copy(shareQuery.data.public_url, 'Fundraiser link')}
              copyTestId={`${PREFIX}-public-url`}
            />
            {/* Short link (go.givmo.io/s/<slug>, the env's go-host). */}
            <ShareLinkRow
              icon={Link2}
              label="Short link"
              value={shareQuery.data.share_url}
              onCopy={() => void copy(shareQuery.data.share_url, 'Short link')}
              copyTestId={`${PREFIX}-short-link`}
            />

            {/* QR downloads (svg / png / pdf). */}
            <div className="flex flex-col gap-8">
              <div className="flex items-center gap-8">
                <Icon icon={QrCode} size={16} className="text-text-secondary" aria-hidden />
                <span className="text-s13 font-w600 text-text-strong">QR code</span>
                {onCustomize && (
                  <Button
                    appearance="saas"
                    tone="link"
                    size="sm"
                    className="ml-auto"
                    leadingIcon={<Icon icon={Palette} size={14} aria-hidden />}
                    onClick={onCustomize}
                    testId={`${PREFIX}-qr-customize`}
                  >
                    Customize
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-8">
                {QR_FORMATS.map((format) => (
                  <Button
                    key={format}
                    appearance="saas"
                    tone="secondary"
                    size="sm"
                    leadingIcon={<Icon icon={Download} size={14} aria-hidden />}
                    loading={downloading === format}
                    disabled={downloading !== null && downloading !== format}
                    onClick={() => void downloadQr(format)}
                    testId={`${PREFIX}-qr-${format}`}
                  >
                    {format.toUpperCase()}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

// ── One copyable link row (read-only value + a copy button). ────────────────────────────────────────
function ShareLinkRow({
  icon,
  label,
  value,
  onCopy,
  copyTestId,
}: {
  icon: typeof Link2;
  label: string;
  value: string;
  onCopy: () => void;
  copyTestId: string;
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-8">
        <Icon icon={icon} size={16} className="text-text-secondary" aria-hidden />
        <span className="text-s13 font-w600 text-text-strong">{label}</span>
      </div>
      <div className="flex items-center gap-8">
        <span
          className="min-w-0 flex-1 truncate rounded-md border border-border-subtle bg-surface-sunken px-10 py-8 text-s13 text-text-secondary"
          title={value}
        >
          {value}
        </span>
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          leadingIcon={<Icon icon={Copy} size={14} aria-hidden />}
          onClick={onCopy}
          testId={copyTestId}
        >
          Copy
        </Button>
      </div>
    </div>
  );
}
